import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import { DeleteEngagementModal } from '@/components/modals/delete-engagement-modal';
import {
  forgetEngagementQueries,
  newLifecycleIdempotencyKey,
  useCanDeleteEngagements,
  useDeleteEngagementMutation,
} from '@/lib/api/engagement-lifecycle';

/**
 * "Delete engagement" on the engagement overview (T-0161), firm admins only.
 *
 * Renders nothing for anyone else: the flag comes from `GET /api/v1/me`, so
 * the screen never decides who is an admin. The button opens the typed-name
 * confirmation; once the server confirms, the user lands on the portfolio
 * with a toast, and everything cached about the engagement is dropped.
 *
 * One Idempotency-Key per opening of the dialog, so a double click or a
 * network retry deletes once.
 */

export interface DeleteEngagementControlProps {
  engagementId: string;
  engagementName: string;
  clientName?: string;
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error && err.message
    ? err.message
    : 'The engagement could not be deleted. Try again.';
}

export function DeleteEngagementControl({
  engagementId,
  engagementName,
  clientName,
}: DeleteEngagementControlProps) {
  const canDelete = useCanDeleteEngagements();
  const [open, setOpen] = React.useState(false);
  const [idempotencyKey, setIdempotencyKey] = React.useState(newLifecycleIdempotencyKey);
  const mutation = useDeleteEngagementMutation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  if (!canDelete) return null;

  const onConfirm = (typedName: string) => {
    mutation.mutate(
      { engagementId, confirmName: typedName, idempotencyKey },
      {
        onSuccess: () => {
          setOpen(false);
          toast({
            message: `${engagementName} was deleted. A firm admin can restore it from Firm settings.`,
            actions: [
              {
                label: 'Deleted engagements',
                onClick: () => void navigate({ to: '/settings/deleted-engagements' }),
              },
            ],
          });
          // Leave the engagement first, then forget it, so nothing on its
          // pages refetches into a 404 on the way out.
          void Promise.resolve(navigate({ to: '/' })).then(() =>
            forgetEngagementQueries(qc, engagementId),
          );
        },
      },
    );
  };

  return (
    <>
      <WorkspaceButton
        variant="destructive"
        onClick={() => {
          mutation.reset();
          setIdempotencyKey(newLifecycleIdempotencyKey());
          setOpen(true);
        }}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
        Delete engagement
      </WorkspaceButton>
      <DeleteEngagementModal
        open={open}
        onOpenChange={setOpen}
        engagementName={engagementName}
        clientName={clientName}
        deleting={mutation.isPending}
        error={errorMessage(mutation.error)}
        onConfirm={onConfirm}
      />
    </>
  );
}
